import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Upload, HelpCircle, MoreHorizontal, Eye, Search, ChevronDown, ChevronUp, Filter, MessageCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { CVFullScreenModal } from "../candidates/CVFullScreenModal";
import { getFormattedDateTime } from "@/utils/settings";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { SendEmailModal } from "@/components/email/SendEmailModal";
import { useTranslation } from "react-i18next";

interface Document {
	id: string;
	name: string;
	category: string | null;
	exclude_from_ai: boolean;
	created_at: string;
	storage_id: string;
	comments: string | null;
}

interface MatchDocumentsTabProps {
	matchId: string;
}

const documentTypes = [
	{ value: "emails", label: "Emails" },
	{ value: "interview", label: "Interview" },
	{ value: "other", label: "Other" },
];

export default function MatchDocumentsTab({ matchId }: MatchDocumentsTabProps) {
	const { t } = useTranslation();
	const [documents, setDocuments] = useState<Document[]>([]);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [loading, setLoading] = useState(true);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [uploadStep, setUploadStep] = useState<"drop" | "details">("drop");
	const [uploadedFile, setUploadedFile] = useState<File | null>(null);
	const [docName, setDocName] = useState("");
	const [docType, setDocType] = useState("emails");
	const [aiAnalysis, setAiAnalysis] = useState(true);
	const [viewingDoc, setViewingDoc] = useState<null | { url: string; name: string }>(null);
	const [search, setSearch] = useState("");
	const [sortBy, setSortBy] = useState("date");
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
	const [typeFilter, setTypeFilter] = useState("all");
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editingDoc, setEditingDoc] = useState<Document | null>(null);
	const [docComment, setDocComment] = useState("");
	const [uploadLoading, setUploadLoading] = useState(false);
	const [openCommentTooltipId, setOpenCommentTooltipId] = useState<string | null>(null);
	const commentIconRefs = useRef<{ [id: string]: HTMLSpanElement | null }>({});
	const commentTooltipRefs = useRef<{ [id: string]: HTMLDivElement | null }>({});

	useEffect(() => {
		if (!openCommentTooltipId) return;
		function handleClick(e: MouseEvent) {
			const icon = commentIconRefs.current[openCommentTooltipId];
			const tooltip = commentTooltipRefs.current[openCommentTooltipId];
			if (icon && icon.contains(e.target as Node)) {
				return;
			}
			if (tooltip && tooltip.contains(e.target as Node)) {
				return;
			}
			setOpenCommentTooltipId(null);
		}
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [openCommentTooltipId]);

	useEffect(() => {
		fetchDocuments();
	}, [matchId]);
	async function fetchDocuments() {
		try {
			setLoading(true);
			const { data, error } = await supabase.functions.invoke(`document-manager?command=get-documents-for-entity&entityId=${matchId}`, {
				method: 'GET',
			});

			if (error)
				throw error;

			const mappedDocuments: Document[] = data.map((doc: any) => ({
				id: doc.id,
				name: doc.display_name || doc.name,
				category: doc.category,
				exclude_from_ai: doc.exclude_from_ai,
				created_at: doc.created_at,
				storage_id: doc.storage_id,
				comments: doc.comments,
			}));

			setDocuments(mappedDocuments);
		} catch (error) {
			console.error('Error fetching documents:', error);
			setDocuments([]);
		} finally {
			setLoading(false);
		}
	}

	// Filter and sort documents
	let filteredDocuments = documents.filter(doc => {
		const matchesSearch =
			doc.name.toLowerCase().includes(search.toLowerCase()) ||
			(doc.category && doc.category.toLowerCase().includes(search.toLowerCase()));
		const matchesType =
			typeFilter === "all" || (doc.category && doc.category.toLowerCase() === typeFilter);
		return matchesSearch && matchesType;
	});

	filteredDocuments = filteredDocuments.sort((a, b) => {
		if (sortBy === "date") {
			const aDate = new Date(a.created_at).getTime();
			const bDate = new Date(b.created_at).getTime();
			return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
		} else if (sortBy === "name") {
			return sortOrder === "asc"
				? a.name.localeCompare(b.name)
				: b.name.localeCompare(a.name);
		} else if (sortBy === "type") {
			const aType = a.category || "";
			const bType = b.category || "";
			return sortOrder === "asc"
				? aType.localeCompare(bType)
				: bType.localeCompare(aType);
		}
		return 0;
	});

	function handleDrop(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (file) {
			setUploadedFile(file);
			setDocName(file.name);
			setDocType("emails");
			setUploadStep("details");
		}
	}	async function handleSave() {
		if (!uploadedFile) return;
		
		setUploadLoading(true);
		try {
			const formData = new FormData();
			formData.append('file', uploadedFile);
			formData.append('entityType', 'match');
			formData.append('entityId', matchId);
			formData.append('documentCategory', docType.toLowerCase());
			formData.append('documentName', docName);
			formData.append('comments', docComment || '');
			formData.append('excludeFromAi', (!aiAnalysis).toString());
			
			const { data, error } = await supabase.functions.invoke('document-manager?command=upload-documents', {
				method: 'POST',
				body: formData
			});
			
			if (error) {
				throw error;
			}
			
			if (data?.success && data?.document) {
				const newDoc: Document = {
					id: data.document.id,
					name: data.document.display_name || data.document.name,
					category: data.document.category,
					exclude_from_ai: data.document.exclude_from_ai || false,
					created_at: data.document.created_at || new Date().toISOString(),
					storage_id: data.document.storage_id,
					comments: data.document.comments,
				};
				
				setDocuments([...documents, newDoc]);
				setUploadModalOpen(false);
				setUploadStep("drop");
				setUploadedFile(null);
				setDocName("");
				setDocType("emails");
				setAiAnalysis(true);
				setDocComment("");
			}
		} catch (error) {
			console.error('Error uploading document:', error);
			alert('Failed to upload document. Please try again.');
		} finally {
			setUploadLoading(false);
		}
	}

	function handleCancel() {
		setUploadModalOpen(false);
		setUploadStep("drop");
		setUploadedFile(null);
		setDocName("");
		setDocType("emails");
		setAiAnalysis(true);
		setDocComment("");
		setUploadLoading(false);
	}

	async function handleDelete(id: string) {
		try {
			const { error } = await supabase.functions.invoke('document-manager?command=delete-document', {
				method: 'POST',
				body: JSON.stringify({
					documentId: id
				})
			});

			if (error)
				throw error;

			setDocuments((docs) => docs.filter((doc) => doc.id !== id));
		} catch (error) {
			console.error('Error deleting document:', error);
			alert('Failed to delete document. Please try again.');
		}
	}

	function handleEdit(id: string) {
		const doc = documents.find((d) => d.id === id);
		if (doc) {
			setEditingDoc(doc);
			setDocName(doc.name);
			setDocType(doc.category || "emails");
			setAiAnalysis(!doc.exclude_from_ai);
			setDocComment(doc.comments || "");
			setEditModalOpen(true);
		}
	}

	async function handleEditSave() {
		if (!editingDoc) return;
		
		try {
			const { data, error } = await supabase.functions.invoke('document-manager?command=update-document', {
				method: 'POST',
				body: JSON.stringify({
					documentId: editingDoc.id,
					name: docName,
					category: docType,
					comments: docComment || null,
					exclude_from_ai: !aiAnalysis
				})
			});

			if (error)
				throw error;

			setDocuments((docs) =>
				docs.map((doc) =>
					doc.id === editingDoc.id
						? { 
							...doc, 
							name: docName, 
							category: docType, 
							comments: docComment || null,
							exclude_from_ai: !aiAnalysis
						}
						: doc
				)
			);

			setEditModalOpen(false);
			setEditingDoc(null);
			setDocName("");
			setDocType("emails");
			setAiAnalysis(false);
			setDocComment("");
		} catch (error) {
			console.error('Error updating document:', error);
			alert('Failed to update document. Please try again.');
		}
	}

	function handleEditCancel() {
		setEditModalOpen(false);
		setEditingDoc(null);
		setDocName("");
		setDocType("emails");
		setAiAnalysis(false);
		setDocComment("");
	}

	async function handleView(storageId: string, documentName: string) {
		try {
			const { data, error } = await supabase.functions.invoke(`document-manager?command=get-document-url&documentStorageId=${storageId}`, {
				method: 'GET',
			});

			if (error) {
				throw error;
			}

			if (data?.signedUrl) {
				const isLocalhost = () => {
					return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
				};

				let url = data.signedUrl;
				if (isLocalhost()) {
					url = url.replace('http://kong:8000', 'http://localhost:54321');
				}

				setViewingDoc({ url, name: documentName });
			} else {
				console.error('No signed URL returned from server');
				alert('Failed to load document preview');
			}
		} catch (error) {
			console.error('Error fetching document URL:', error);
			alert('Failed to load document preview');
		}
	}

	function handleSendByEmail(_id: string) {
		setShowEmailModal(true);
	}
	return (
		<div>
			{/* Modern search/filter bar */}
			<div className="flex flex-wrap items-center gap-3 mb-4 w-full">
				<div className="relative flex items-center flex-1 min-w-[220px] max-w-md">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
					<Input
						type="text"
						placeholder={t('documents.searchPlaceholder')}
						value={search}
						onChange={e => setSearch(e.target.value)}
						className="pl-10 pr-3 py-2 rounded-sm bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 shadow-none"
					/>
				</div>
				<div className="flex items-center gap-2">
					<span className="text-gray-500 dark:text-gray-400 text-sm font-medium">{t('documents.sortBy')}:</span>
					<Select value={sortBy} onValueChange={setSortBy}>
						<SelectTrigger className="w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm px-4 py-2 text-sm">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="date">{t('documents.dateAdded')}</SelectItem>
							<SelectItem value="name">{t('documents.name')}</SelectItem>
							<SelectItem value="type">{t('documents.type')}</SelectItem>
						</SelectContent>
					</Select>
					<Button
						variant="outline"
						size="icon"
						className="rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
						onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
						aria-label="Toggle sort order"
					>
						{sortOrder === "asc" ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
					</Button>
				</div>
				<div className="flex items-center gap-2">
					<span className="text-gray-500 dark:text-gray-400 text-sm font-medium">{t('documents.type')}:</span>
					<Select value={typeFilter} onValueChange={setTypeFilter}>
						<SelectTrigger className="w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm px-4 py-2 text-sm">
							<SelectValue>{typeFilter === "all" ? "All" : typeFilter}</SelectValue>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All</SelectItem>
							<SelectItem value="emails">Emails</SelectItem>
							<SelectItem value="interview">Interview</SelectItem>
							<SelectItem value="other">Other</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>
			<div className="flex justify-end mb-4">
				<Button onClick={() => setUploadModalOpen(true)}>
					<Upload className="mr-2 h-4 w-4" /> {t('documents.uploadDocument')}
				</Button>
			</div>
			<div className="overflow-x-auto">
				<table className="min-w-full bg-white dark:bg-gray-800 border rounded-sm">
					<thead>
						<tr className="bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
							<th className="w-10"></th>
							<th className="py-2 px-3">{t('documents.name')}</th>
							<th className="py-2 px-3">{t('documents.type')}</th>
							<th className="py-2 px-3">{t('documents.aiAnalysis')}</th>
							<th className="py-2 px-3">{t('documents.dateAdded')}</th>
							<th className="py-2 px-3">{t('documents.view')}</th>
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={6} className="text-center text-gray-500 dark:text-gray-400 py-6">{t('documents.loadingDocuments')}</td>
							</tr>
						) : filteredDocuments.length === 0 ? (
							<tr>
								<td colSpan={6} className="text-center text-gray-500 dark:text-gray-400 py-6">{t('documents.noDocumentsFound')}</td>
							</tr>
						) : (
							filteredDocuments.map((doc) => (
								<tr key={doc.id} className="border-b last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800 group">
									<td className="pl-2 pr-1 align-middle">
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="icon" className="h-7 w-7 p-0">
													<MoreHorizontal className="h-4 w-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="start">
												<DropdownMenuItem onClick={() => handleEdit(doc.id)}>{t('documents.edit')}</DropdownMenuItem>
												<DropdownMenuItem onClick={() => handleSendByEmail(doc.id)}>{t('documents.sendByEmail')}</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem onClick={() => handleDelete(doc.id)} className="text-red-600">{t('documents.delete')}</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</td>
									<td className="py-2 px-3 align-middle flex items-center gap-2">
										<FileText className="h-4 w-4 text-gray-400" />
										<span className="font-medium">{doc.name}</span>
										{doc.comments && (
											<TooltipProvider>
												<Tooltip open={openCommentTooltipId === doc.id} onOpenChange={() => {}}>
													<TooltipTrigger asChild>
														<span
															ref={el => (commentIconRefs.current[doc.id] = el)}
															className="ml-1 cursor-pointer text-blue-500"
															onClick={e => {
																e.stopPropagation();
																setOpenCommentTooltipId(openCommentTooltipId === doc.id ? null : doc.id);
															}}
															tabIndex={0}
															role="button"
															aria-label="Show comment"
														>
															<MessageCircle className="h-4 w-4" />
														</span>
													</TooltipTrigger>
													{openCommentTooltipId === doc.id && (
														<TooltipContent
															ref={el => (commentTooltipRefs.current[doc.id] = el)}
															side="top"
															align="center"
															className=""
														>
															{doc.comments}
														</TooltipContent>
													)}
												</Tooltip>
											</TooltipProvider>
										)}
									</td>
									<td className="py-2 px-3 align-middle">{doc.category || "N/A"}</td>
									<td className="py-2 px-3 align-middle text-center">
										<Checkbox checked={!doc.exclude_from_ai} disabled className="pointer-events-none" />
									</td>
									<td className="py-2 px-3 align-middle">{getFormattedDateTime(doc.created_at, undefined, undefined, false)}</td>
									<td className="py-2 px-3 align-middle">
										<Button variant="outline" size="icon" onClick={() => handleView(doc.storage_id, doc.name)}>
											<Eye className="h-4 w-4" />
										</Button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>			{/* Upload Modal */}
			<Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
				<DialogContent className="max-w-2xl w-full p-8">
					{uploadStep === "drop" && (
						<>
							<DialogHeader>
								<DialogTitle>{t('documents.uploadDocument')}</DialogTitle>
							</DialogHeader>
							<div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-sm p-8 mb-4">
								<Upload className="h-8 w-8 text-gray-400 mb-2" />
								<div className="mb-2 text-gray-600 dark:text-gray-300">{t('documents.dragAndDrop')}</div>
								<Input type="file" className="mt-2" onChange={handleDrop} />
							</div>
							<DialogFooter>
								<Button variant="outline" onClick={handleCancel}>{t('documents.cancel')}</Button>
							</DialogFooter>
						</>
					)}
					{uploadStep === "details" && (
						<>
							<DialogHeader>
								<DialogTitle>{t('documents.documentDetails')}</DialogTitle>
							</DialogHeader>
							<div className="space-y-4">
								<div>
									<Label htmlFor="docName">{t('documents.documentName')}</Label>
									<Input
										id="docName"
										value={docName}
										onChange={(e) => setDocName(e.target.value)}
										className="mt-1"
									/>
								</div>
								<div>
									<Label htmlFor="docType">{t('documents.documentType')}</Label>
									<Select value={docType} onValueChange={setDocType}>
										<SelectTrigger className="w-full mt-1">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{documentTypes.map((type) => (
												<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div>
									<Label htmlFor="docComment">{t('documents.comment')}</Label>
									<textarea
										id="docComment"
										value={docComment}
										onChange={e => setDocComment(e.target.value)}
										className="mt-1 w-full min-h-[60px] border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-transparent"
										placeholder={t('documents.addCommentPlaceholder')}
									/>
								</div>
								<div className="flex items-center gap-2">
									<Checkbox 
										id="aiAnalysis" 
										checked={aiAnalysis} 
										onCheckedChange={(checked) => setAiAnalysis(!!checked)}
									/>
									<Label htmlFor="aiAnalysis" className="mb-0">{t('documents.aiAnalysis')}</Label>
									<div className="relative group">
										<HelpCircle className="h-4 w-4 text-gray-400 ml-1 cursor-pointer" />
										<div className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg p-2 text-xs text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 pointer-events-none z-10">
											{t('documents.aiAnalysisImpact')}
										</div>
									</div>
								</div>
							</div>
							<DialogFooter className="mt-4">
								<Button variant="outline" onClick={handleCancel} disabled={uploadLoading}>{t('documents.cancel')}</Button>
								<Button onClick={handleSave} disabled={uploadLoading}>
									{uploadLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
									{uploadLoading ? t('documents.uploading') : t('documents.save')}
								</Button>
							</DialogFooter>
						</>
					)}
				</DialogContent>
			</Dialog>

			{/* Document Viewer */}
			{viewingDoc && (
				<CVFullScreenModal
					cvUrl={viewingDoc.url}
					name={viewingDoc.name}
					onClose={() => setViewingDoc(null)}
				/>
			)}

			{/* Edit Modal */}
			{editModalOpen && editingDoc && (
				<Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
					<DialogContent className="max-w-2xl w-full p-8">
						<DialogHeader>
							<DialogTitle>{t('documents.editDocument')}</DialogTitle>
						</DialogHeader>
						<div className="space-y-4">
							<div>
								<Label htmlFor="editDocName">{t('documents.documentName')}</Label>
								<Input
									id="editDocName"
									value={docName}
									onChange={(e) => setDocName(e.target.value)}
									className="mt-1"
								/>
							</div>
							<div>
								<Label htmlFor="editDocType">{t('documents.documentType')}</Label>
								<Select value={docType} onValueChange={setDocType}>
									<SelectTrigger className="w-full mt-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{documentTypes.map((type) => (
											<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label htmlFor="editDocComment">{t('documents.comment')}</Label>
								<textarea
									id="editDocComment"
									value={docComment}
									onChange={e => setDocComment(e.target.value)}
									className="mt-1 w-full min-h-[60px] border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm bg-transparent"
									placeholder={t('documents.addCommentPlaceholder')}
								/>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox 
									id="editAiAnalysis" 
									checked={aiAnalysis} 
									onCheckedChange={(checked) => setAiAnalysis(!!checked)}
								/>
								<Label htmlFor="editAiAnalysis" className="mb-0">{t('documents.aiAnalysis')}</Label>
								<div className="relative group">
									<HelpCircle className="h-4 w-4 text-gray-400 ml-1 cursor-pointer" />
									<div className="absolute left-1/2 -translate-x-1/2 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg p-2 text-xs text-gray-700 dark:text-gray-200 opacity-0 group-hover:opacity-100 pointer-events-none z-10">
										{t('documents.aiAnalysisImpact')}
									</div>
								</div>
							</div>
						</div>
						<DialogFooter className="mt-4">
							<Button variant="outline" onClick={handleEditCancel}>{t('documents.cancel')}</Button>
							<Button onClick={handleEditSave}>{t('documents.save')}</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}
			<SendEmailModal open={showEmailModal} onClose={() => setShowEmailModal(false)} />
		</div>
	);
}
